import type { PixelEvent } from '@/events';

import type { DestinationAdapter } from './types';

/**
 * Registry of destination adapters — mirror of ModuleRegistry pattern.
 * No switch/if cascades; support decisions live inside each adapter.
 */
export class DestinationRegistry {
  private readonly adapters = new Map<string, DestinationAdapter>();

  register(destination: DestinationAdapter): void {
    if (this.adapters.has(destination.name)) {
      throw new Error(
        `[Integration] Destination "${destination.name}" is already registered`
      );
    }
    this.adapters.set(destination.name, destination);
  }

  remove(name: string): boolean {
    const existing = this.adapters.get(name);
    if (!existing) return false;
    existing.destroy?.();
    return this.adapters.delete(name);
  }

  get(name: string): DestinationAdapter | undefined {
    return this.adapters.get(name);
  }

  getAll(): readonly DestinationAdapter[] {
    return [...this.adapters.values()];
  }

  supports(event: PixelEvent): readonly DestinationAdapter[] {
    return this.getAll().filter(adapter => {
      try {
        return adapter.supports(event);
      } catch {
        return false;
      }
    });
  }

  has(name: string): boolean {
    return this.adapters.has(name);
  }

  size(): number {
    return this.adapters.size;
  }

  clear(): void {
    for (const adapter of this.adapters.values()) {
      adapter.destroy?.();
    }
    this.adapters.clear();
  }
}
